/**
 * GoalDetailDialog — centered modal detail view for a goal.
 *
 * Replaces the former inline row-expansion (Story 18.8 AC#9 originally chose an
 * inline panel "never a slide-over"). Clicking a goal — table row OR grid card —
 * now opens this pop-up. The app's blessed pop-up convention is a CENTERED modal
 * Dialog (UI design-system §4.1), so this brings the goals surface in line with
 * every other detail/confirm surface (Add/Edit Client, Delete Goal, …) instead
 * of the bespoke drop-down expansion.
 *
 * Built on the same @radix-ui/react-dialog scaffold as DeleteGoalDialog:
 * bg-black/35 overlay, rounded-3xl border-[0.5px] shadow-lg content, p-8,
 * max-h-[85vh] scroll, AnimatePresence exit. Sits at z-50 — one tier BELOW the
 * z-[60] action dialogs (edit/delete) it can open, so those stack cleanly on top
 * during the close→open handoff.
 *
 * Width is max-w-xl (§4.3 medium tier): the body (GoalDetailPanel) renders a
 * 2-column stat grid PLUS the 18.7 history chart — a read-oriented detail
 * surface with side-by-side sections, not a cramped form, so it earns the wider
 * tier rather than the default max-w-md.
 *
 * Edit/Delete close this dialog before invoking their handler (you can't stack a
 * second modal cleanly); Pause/Resume is an inline mutation and leaves it open.
 */

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "motion/react";
import {
  displayStatus,
  GOAL_STATUS_BADGES,
  type GoalListItem,
} from "../../lib/goalPageDisplay";
import { GoalDetailPanel } from "./GoalDetailPanel";

interface GoalDetailDialogProps {
  goal: GoalListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canManage: boolean;
  onEdit: (goal: GoalListItem) => void;
  onTogglePause: (goal: GoalListItem) => void;
  onDelete: (goal: GoalListItem) => void;
}

export function GoalDetailDialog({
  goal,
  open,
  onOpenChange,
  canManage,
  onEdit,
  onTogglePause,
  onDelete,
}: GoalDetailDialogProps) {
  const badge = goal ? GOAL_STATUS_BADGES[displayStatus(goal)] : null;
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && goal && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                data-testid={`goal-detail-dialog-${goal.id}`}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    {goal.name}
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                    asChild
                  >
                    <div>
                      <span className="truncate">{goal.clientName}</span>
                      {badge && (
                        <span
                          className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${badge.badge}`}
                          data-testid={`goal-detail-status-${goal.id}`}
                        >
                          {badge.label}
                        </span>
                      )}
                    </div>
                  </DialogPrimitive.Description>
                </div>

                <div className="mt-6">
                  <GoalDetailPanel
                    goal={goal}
                    canManage={canManage}
                    onEdit={() => {
                      onOpenChange(false);
                      onEdit(goal);
                    }}
                    onTogglePause={() => onTogglePause(goal)}
                    onDelete={() => {
                      onOpenChange(false);
                      onDelete(goal);
                    }}
                  />
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
